/**
 * Acesso de quem está usando o sistema (spec 20261019-gestao-de-acessos, RF02, RF06, RF10).
 *
 * Lê `/api/acessos/me/` uma vez e responde `pode(tela, nivel)`. A tela segue o modo do
 * servidor:
 *
 * - `aplicar`: o que a role não permite some do menu, a rota mostra "Sem acesso" e quem está
 *   Aguardando/Inativo vê a tela de bloqueio;
 * - `observar` e `desligado`: nada é escondido nem bloqueado — o servidor só registra —, e o
 *   acesso real fica disponível em `permissoesReais` para quem quiser conferir.
 *
 * Fora do provedor (testes de componente) ou com o servidor sem a gestão de acessos, o acesso é
 * total, como antes dela existir.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { apiFetch } from '../utils/apiClient';

/** Mesma escada do backend (`acessos/permissoes.py`). */
export const ORDEM_NIVEL = { nenhum: 0, ver: 1, sim: 1, simular: 2, editar: 2 };

const ordem = (nivel) => ORDEM_NIVEL[nivel] ?? 0;

/** `pode(tela, nivel)` sobre um mapa de permissões `{tela: nivel}`. */
export const podeCom = (permissoes) => (tela, nivel = 'ver') => ordem(permissoes?.[tela]) >= ordem(nivel);

/**
 * Alguma das exigências é atendida. Cada exigência é `'tela'` (nível ver) ou `['tela', 'nivel']`.
 */
export const atende = (pode, exigencias) => !exigencias?.length || exigencias.some((e) => (
    Array.isArray(e) ? pode(e[0], e[1]) : pode(e)
));

const TOTAL = () => true;

export const ACESSO_PADRAO = {
    carregando: false,
    modo: 'desligado',
    aplicando: false,
    autenticado: false,
    usuario: null,
    grupos: [],
    roles: [],
    recorte: { redeInteira: true, ufs: [] },
    contato: '',
    telas: {},
    bloqueio: null,
    pode: TOTAL,
    permissoesReais: {},
    recarregar: () => {},
};

const AcessoContext = createContext(ACESSO_PADRAO);

/** O valor do contexto a partir da resposta de `/me/` (exportado para os testes). */
export const montarAcesso = (me, { carregando = false, recarregar = () => {} } = {}) => {
    if (!me) return { ...ACESSO_PADRAO, carregando, recarregar };
    const aplicando = me.modo === 'aplicar';
    const status = me.usuario?.status;
    let bloqueio = null;
    if (aplicando) {
        if (!me.autenticado) bloqueio = 'sem_token';
        else if (status === 'inativo') bloqueio = 'inativo';
        else if (status !== 'ativo') bloqueio = 'aguardando';
    }
    const permissoesReais = me.permissoes || {};
    return {
        carregando,
        modo: me.modo,
        aplicando,
        autenticado: Boolean(me.autenticado),
        usuario: me.usuario || null,
        grupos: me.grupos || [],
        roles: me.roles || [],
        recorte: me.recorte || ACESSO_PADRAO.recorte,
        contato: me.contato || '',
        telas: me.telas || {},
        bloqueio,
        pode: aplicando ? podeCom(permissoesReais) : TOTAL,
        permissoesReais,
        recarregar,
    };
};

export function AcessoProvider({ children }) {
    const [estado, setEstado] = useState({ carregando: true, me: null });

    const carregar = useCallback(async () => {
        try {
            const resposta = await apiFetch('/api/acessos/me/');
            if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);
            setEstado({ carregando: false, me: await resposta.json() });
        } catch {
            // Servidor sem a gestão de acessos (ou fora do ar): segue como antes dela
            setEstado({ carregando: false, me: null });
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const valor = useMemo(
        () => montarAcesso(estado.me, { carregando: estado.carregando, recarregar: carregar }),
        [estado, carregar],
    );
    return <AcessoContext.Provider value={valor}>{children}</AcessoContext.Provider>;
}

AcessoProvider.propTypes = { children: PropTypes.node };

export const useAcesso = () => useContext(AcessoContext);

/** Para testes e para a "pré-visualização como" da página Acessos. */
export const AcessoFixo = ({ valor, children }) => (
    <AcessoContext.Provider value={valor}>{children}</AcessoContext.Provider>
);

AcessoFixo.propTypes = { valor: PropTypes.object.isRequired, children: PropTypes.node };
